import { HubspotForm } from '@/components/common/hubspot-form';
import { createIntlTranslations } from 'Features/Intl/fns';
import { Button } from 'Features/SRP/Components/Button';
import { Modal } from 'Features/SRP/Components/Modal';
import { Footer } from 'Features/Shared/Components/Footer';
import { Navbar } from 'Features/Shared/Components/Navbar';
import { GetServerSidePropsContext } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { NextSeo } from 'next-seo';
import { useTranslation } from 'react-i18next';

interface Props {
  contactFormId: string;
}

export default function Page({ contactFormId }: Props) {
  const { t } = useTranslation('services');

  const clusters = t('clusters') as Array<{
    title: string;
    content: Array<{ headline: string; content: string }>;
  }>;

  return (
    <>
      <NextSeo title={t('meta.title')} description={t('meta.description')} />

      <Navbar />

      {/* Content */}
      <div className="max-w-7xl mx-auto min-h-[calc(100vh-6rem)] px-6">
        <div className="mx-auto max-w-2xl py-32 sm:py-24">
          <div className="text-center">
            <h1 className="text-4xl font-space font-bold tracking-tight text-slate-900 sm:text-6xl">
              {t('heading')}
            </h1>
            <p className="mt-6 text-lg leading-8 text-slate-600">{t('subheading')}</p>
          </div>
        </div>

        <div className="mx-auto">
          <div className="grid grid-cols-1 gap-x-8 gap-y-10 lg:max-w-none lg:grid-cols-4 lg:gap-y-16">
            {clusters.map((cluster, idx) => (
              <div key={idx}>
                <div className="flex items-center justify-center bg-slate-100 h-10 w-10 rounded-xl mb-3">
                  <span className="text-xl font-semibold">{idx + 1}</span>
                </div>
                <div className="text-base font-semibold leading-7 text-slate-900">
                  {cluster.title}
                </div>

                <Modal
                  trigger={
                    <Button size="sm" variant="blue" className="mt-5">
                      {t('extras.learn')}
                    </Button>
                  }
                  content={
                    <div className="h-full space-y-5">
                      <div>
                        <h3 className="text-xl font-semibold text-slate-900">{cluster.title}</h3>
                        <ul className="list-disc ml-4 mt-5 space-y-3">
                          {cluster.content.map((chunk, idx) => (
                            <li key={idx}>
                              <p className="text-base leading-relaxed text-slate-700">
                                <span className="font-semibold">{chunk.headline}</span>:{' '}
                                {chunk.content}
                              </p>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  }
                />
              </div>
            ))}
          </div>
        </div>

        <section className="my-24">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="mx-auto grid max-w-lg grid-cols-4 items-center gap-x-8 gap-y-10 sm:max-w-xl sm:grid-cols-6 sm:gap-x-10 lg:mx-0 lg:max-w-none lg:grid-cols-5">
              <img
                className="col-span-2 max-h-24 w-full object-contain lg:col-span-1"
                src="/assets/img/collaborate/fimaa.jpg"
                alt="Fimaa"
              />
              <img
                className="col-span-2 max-h-24 w-full object-contain lg:col-span-1"
                src="/assets/img/collaborate/casavo.jpg"
                alt="Casavo"
                width="158"
                height="48"
              />
              <img
                className="col-span-2 max-h-24 w-full object-contain lg:col-span-1"
                src="/assets/img/collaborate/miplan.jpg"
                alt="Miplan"
                width="158"
                height="48"
              />
              <img
                className="col-span-2 max-h-24 w-full object-contain sm:col-start-2 lg:col-span-1"
                src="/assets/img/collaborate/chiusano.jpg"
                alt="Chiusano"
                width="158"
                height="48"
              />
              <img
                className="col-span-2 col-start-2 max-h-24 w-full object-contain sm:col-start-auto lg:col-span-1"
                src="/assets/img/collaborate/greenstonefunds.jpg"
                alt="GreenStoneFunds"
                width="158"
                height="48"
              />
            </div>
          </div>
        </section>

        <div className="h-36 w-[2px] bg-slate-200 mx-auto rounded-xl"></div>

        <section className="my-24">
          <div className="max-w-xl mx-auto">
            <p className="text-3xl text-center font-bold font-space tracking-tight text-slate-900 sm:text-4xl">
              {t('contact.heading')}
            </p>
            <p className="mt-6 text-lg leading-8 text-center text-slate-600">
              {t('contact.subheading')}
            </p>
          </div>

          <div className="mt-10">
            <HubspotForm formId={contactFormId} id="contact-form" />
          </div>
        </section>
      </div>

      <Footer />
    </>
  );
}

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const locale = context.locale?.startsWith('it') ? 'it' : 'en';
  const intl = await createIntlTranslations(locale, 'sell');
  const i18n = await serverSideTranslations(locale, ['common', 'services']);

  const contactFormId =
    context.locale === 'it'
      ? '969dc0dd-97be-45ef-8f1c-ca11518b7951'
      : 'c916f655-78ea-45bb-941c-c46de2a88049';

  return {
    props: { ...i18n, contactFormId, ...intl },
  };
}
